import React, { useEffect, useState } from "react";
import { api, monthLabel, niceDate } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { Check } from "@/components/Check";
import { GoogleButton } from "@/components/GoogleButton";
import { SecuritySection } from "@/components/SecuritySection";
import { NotificationSection } from "@/components/NotificationSection";
import { AccessManager } from "@/components/AccessManager";
import { Save, ExternalLink, Link2Off, Check as CheckIcon, CalendarPlus, Download, Mail } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  const [gStatus, setGStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
    api.get("/auth/google/status").then((r) => setGStatus(r.data)).catch(() => {});
  }, []);

  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    await api.put("/settings", next);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  if (!s) return null;

  const exportAll = async () => {
    setExporting(true);
    try {
      const res = await api.get("/export/all", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `command-center-backup-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      alert("Export failed — please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings">
      <header>
        <div className="kpi-label">System</div>
        <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
          Settings
        </h1>
      </header>

      <section className="hud-frame p-6 space-y-5" data-testid="settings-google">
        <div className="kpi-label flex items-center gap-2">
          <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
          </svg>
          GOOGLE CONNECTION
        </div>
        {gStatus?.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="hud-chip hud-chip-on">
                <CheckIcon size={10} /> Connected
              </span>
              <span className="mono text-[10px] tracking-widest text-muted-foreground uppercase truncate">
                {gStatus.google_email}
              </span>
            </div>
            {gStatus.has_calendar_access ? (
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Calendar.events scope · ready to push deal/media events to your Google Calendar
              </div>
            ) : (
              <button
                onClick={() => {
                  window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/start?calendar=1&next=/settings`;
                }}
                className="hud-btn flex items-center gap-2"
                data-testid="settings-enable-calendar"
              >
                <CalendarPlus size={11} /> Enable Calendar Sync
              </button>
            )}
            {user?.password_hash !== "" && (
              <button
                onClick={async () => {
                  if (!window.confirm("Disconnect Google? You'll still be able to sign in with your password.")) return;
                  try {
                    await api.post("/auth/google/disconnect");
                    const r = await api.get("/auth/google/status");
                    setGStatus(r.data);
                  } catch (e) {
                    alert(e.response?.data?.detail || "Could not disconnect");
                  }
                }}
                className="hud-btn"
                data-testid="settings-google-disconnect"
              >
                <Link2Off size={11} /> Disconnect Google
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Connect your Google account to sign in faster and push your deal follow-ups, media shoots,
              and weekly tasks straight to Google Calendar.
            </p>
            <GoogleButton next="/settings" label="Connect Google Account" />
          </div>
        )}
      </section>

      <SecuritySection />

      <NotificationSection />

      <section className="hud-frame p-6 space-y-5">
        <div className="kpi-label">CYCLES</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="kpi-label">SELECTED MONTH</label>
            <input
              type="month"
              value={s.selected_month}
              onChange={(e) => save({ selected_month: e.target.value })}
              className="hud-input mt-2"
              data-testid="settings-month"
            />
            <div className="mono text-[11px] text-muted-foreground mt-2">
              {monthLabel(s.selected_month)}
            </div>
          </div>
          <div>
            <label className="kpi-label">SELECTED WEEK START (MON)</label>
            <input
              type="date"
              value={s.selected_week_start}
              onChange={(e) => save({ selected_week_start: e.target.value })}
              className="hud-input mt-2"
              data-testid="settings-week"
            />
            <div className="mono text-[11px] text-muted-foreground mt-2">
              {niceDate(s.selected_week_start)}
            </div>
          </div>
        </div>
      </section>

      <section className="hud-frame p-6 space-y-5">
        <div className="kpi-label">APPEARANCE</div>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm">Theme</div>
            <div className="mono text-[11px] text-muted-foreground tracking-widest uppercase">
              {theme === "dark" ? "Noir · Charcoal & Oxblood" : "Parchment · Cream & Burgundy"}
            </div>
          </div>
          <button onClick={toggle} className="hud-btn" data-testid="settings-theme-toggle">
            SWITCH TO {theme === "dark" ? "LIGHT" : "DARK"}
          </button>
        </div>
      </section>

      <section className="hud-frame p-6 space-y-5">
        <div className="kpi-label">ARCHIVE</div>
        <div className="flex items-center gap-3">
          <Check
            checked={s.archive_old_weeks}
            onChange={(v) => save({ archive_old_weeks: v })}
            testid="settings-archive"
          />
          <div>
            <div className="text-sm">Archive old weeks</div>
            <div className="mono text-[11px] text-muted-foreground tracking-widest uppercase">
              Past weeks stay browsable in Weekly OS via the &lt; ARROW
            </div>
          </div>
        </div>
        <div>
          <label className="kpi-label">DELETE OLD DATA</label>
          <select
            value={s.delete_old_data}
            onChange={(e) => save({ delete_old_data: e.target.value })}
            className="hud-input mt-2"
            data-testid="settings-delete-policy"
          >
            <option value="Never">Never</option>
            <option value="After 1 year">After 1 year</option>
            <option value="After 2 years">After 2 years</option>
          </select>
        </div>
      </section>

      <section className="hud-frame p-6 space-y-5" data-testid="settings-data">
        <div className="kpi-label">DATA</div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm">Export all my data</div>
            <div className="mono text-[11px] text-muted-foreground tracking-widest uppercase">
              One ZIP with your pipeline + every tab as CSV
            </div>
          </div>
          <button
            onClick={exportAll}
            disabled={exporting}
            className="hud-btn flex items-center gap-2"
            data-testid="settings-export-all"
          >
            <Download size={12} /> {exporting ? "PREPARING…" : "EXPORT ZIP"}
          </button>
        </div>
        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <Check
            checked={!!s.weekly_backup_email}
            onChange={(v) => save({ weekly_backup_email: v })}
            testid="settings-weekly-backup"
          />
          <div className="flex items-start gap-2">
            <Mail size={13} className="text-muted-foreground mt-0.5" />
            <div>
              <div className="text-sm">Weekly backup email</div>
              <div className="mono text-[11px] text-muted-foreground tracking-widest uppercase">
                Every Sunday, email me a CSV of my pipeline
              </div>
            </div>
          </div>
        </div>
      </section>

      {user?.is_master && <AccessManager />}

      <section className="hud-frame p-6 space-y-3">
        <div className="kpi-label">Shortcuts</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mono text-xs">
          <div className="flex justify-between"><span>Jump to section</span><span className="text-muted-foreground">1 – 8</span></div>
          <div className="flex justify-between"><span>Command Deck</span><span className="text-muted-foreground">F</span></div>
          <div className="flex justify-between"><span>Quick search</span><span className="text-muted-foreground">⌘ / Ctrl + K</span></div>
          <div className="flex justify-between"><span>Export current view</span><span className="text-muted-foreground">CSV button</span></div>
        </div>
      </section>

      {saved && (
        <div className="fixed bottom-6 right-6 hud-btn hud-btn-primary flex items-center gap-2 animate-fadein z-40">
          <Save size={12} /> SAVED
        </div>
      )}
    </div>
  );
}
